<template>
  <div class="accordion-item" @mouseover="upHere = true" @mouseleave="upHere = false">
    <div class="accordion-item-header">
      <div class="header-buttons">
        <Button class="button active" @click="toggleExpand">
          <md-icon :name="expanded ? 'chevron-up' : 'chevron-down'" />
        </Button>
      </div>
      <div class="title">
        <div class="prefix">C</div>
        <span>CHAT</span>
      </div>
      <div v-if="upHere && !isBusy" class="header-buttons">
        <div class="actions">
          <Button class="button" :class="{ active }" :disabled="isBusy" title="Delete" @click="deleteChat">
            <md-icon name="delete" />
          </Button>
        </div>
      </div>
    </div>
    <div v-if="expanded" class="accordion-item-content">
      <div class="settings">
        <div class="model">
          <Multiselect
            v-model="localSettings.model"
            label="Model"
            :searchable="false"
            :options="modelOptions"
            :disabled="isBusy"
            :get-label-callback="id => getLabel(id)"
            @input="onChange"
          />
          <span v-if="!isModelAllowed(localSettings.model)" class="error"
            >Not all organizations this template is assigned for allow selected model</span
          >
        </div>
        <ul class="params">
          <li><TextField v-model="localSettings.temperature" :disabled="isBusy" label="Temperature" type="number" @input="onChange" /></li>
          <li><TextField v-model="localSettings.max_tokens" :disabled="isBusy" label="Max tokens" type="number" @input="onChange" /></li>
          <li v-for="(param, index) in localSettings.params" :key="index" class="param">
            <Multiselect
              v-if="param.options"
              v-model="param.value"
              :label="param.name"
              :options="param.options"
              :disabled="isBusy"
              @input="onChange"
            />
            <TextField v-else v-model="param.value" :label="param.name" :disabled="isBusy" @input="onChange" />
          </li>
        </ul>
        <div class="history">
          <Checkbox v-model="localSettings.includeHistory" :disabled="isBusy" label="Include History" @input="onChange" />
        </div>
        <div v-if="getTools(localSettings.model)" class="functions">
          <Button color="primary" title="Functions" @click="toggleFunctions">
            <div>
              <MdIcon size="sm" name="function-variant" />
              {{ getTools(localSettings.model).filter(f => f.selected).length }} attached
              <MdIcon size="sm" :name="functionsExpanded ? 'menu-up' : 'menu-down'" />
            </div>
          </Button>
        </div>
        <div v-if="functionsExpanded" class="functions" :class="{ expanded: functionsExpanded }">
          <div v-if="!isAllToolsAllowedByOrganizations()">
            <span class="error">Not all tools are allowed by the organizations assigned to this chain.</span>
          </div>
          <ul class="list">
            <li v-for="func in getTools(localSettings.model)" :key="func.name" class="function">
              <Checkbox v-model="func.selected" :disabled="!func.isAllowed" @change="s => functionChecked(func.name, s)" />
              <span :class="{ 'not-allowed': !func.isAllowed }" :title="!func.isAllowed ? 'This tool is not allowed for this model' : ''">
                <label> {{ func.name }} </label>({{ !!func.userDescritpion ? func.userDescritpion : func.description }})
              </span>
            </li>
          </ul>
        </div>
        <div class="skills-selects">
          <Multiselect
            v-model="localSettings.skills"
            :options="skillOptions"
            :get-label-callback="getSkillLabel"
            :disabled="isBusy"
            label="Allowed Skills"
            placeholder="Select allowed skills (optional)"
            :searchable="true"
            :multiple="true"
            @input="onChange"
          />
          <Multiselect
            v-model="localSettings.activeSkills"
            :options="skillOptions"
            :get-label-callback="getSkillLabel"
            :disabled="isBusy"
            label="Active Skills"
            placeholder="Select active skills (optional)"
            :searchable="true"
            :multiple="true"
            @input="onChange"
          />
          <div v-if="hasSkillOverlap" class="error">Warning: some skills appear in both Allowed and Active lists.</div>
        </div>
      </div>

      <div class="prompt-body-content">
        <div v-for="(message, messageIndex) in localSettings.messages" :key="messageIndex" class="template-content">
          <div class="head">
            <span class="label">{{ message.role }}:</span>
          </div>
          <Code
            v-model="message.content"
            :read-only="!$hasPermission('prompts.write') || isBusy"
            :language="'plaintext'"
            class="editor"
            :auto-height="true"
            @input="onChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Button from '@/components/common/Button';
import MdIcon from '@/components/common/MdIcon';
import Code from '@/components/common/Code';
import Multiselect from '@/components/common/Multiselect';
import TextField from '@/components/common/TextField';
import Checkbox from '@/components/common/Checkbox';

export default {
  components: {
    Button,
    MdIcon,
    Code,
    Multiselect,
    TextField,
    Checkbox
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localSettings: {
        ...this.settings,
        functions: this.settings?.functions ?? [],
        skills: this.settings?.skills ?? [],
        activeSkills: this.settings?.activeSkills ?? [],
        includeHistory: this.settings.includeHistory ?? false,
        params: this.settings.params ?? []
      },
      organizations: this.item.organizations ?? [],
      expanded: false,
      functionsExpanded: false,
      upHere: false
    };
  },
  computed: {
    ...mapState({
      sessionContext: s => s.prompts.sample.sessionContext,
      active: state => !state.prompts.sample.isRequestPending,
      modelPermissions: state => state.nlp.modelPermissions ?? [],
      tools: state => state.nlp.tools ?? [],
      providers: state => state.nlp.providers ?? [],
      skills: state => state.nlp.skills ?? []
    }),
    ...mapGetters({
      models: 'nlp/chatModels',
      isBusy: 'prompts/isBusy'
    }),
    selectedModel() {
      return this.models.find(m => m.id === this.localSettings.model);
    },
    skillOptions() {
      return this.skills.map(s => s.name);
    },
    hasSkillOverlap() {
      const allowed = this.localSettings.skills ?? [];
      const preloaded = this.localSettings.activeSkills ?? [];
      return allowed.some(s => preloaded.includes(s));
    },
    modelOptions() {
      return ['', ...this.models.map(m => m.id)];
    },
    allowedModels() {
      const organizations = this.organizations ?? [];

      if (!organizations.length) {
        return [''];
      }

      const allowedModels = [];

      const organizationsPermisssions = this.modelPermissions.filter(mp => organizations.includes(mp.organization));

      for (const model of new Set(organizationsPermisssions.map(mp => mp.model))) {
        if (organizationsPermisssions.filter(op => op.model === model).length === organizations.length) {
          allowedModels.push(model);
        }
      }

      return ['', ...allowedModels];
    }
  },
  watch: {
    skillOptions(newOptions) {
      if (!newOptions.length) return;
      const valid = new Set(newOptions);
      const removedFromAllowed = (this.localSettings.skills ?? []).filter(s => !valid.has(s));
      const removedFromPreloaded = (this.localSettings.activeSkills ?? []).filter(s => !valid.has(s));
      const removed = [...new Set([...removedFromAllowed, ...removedFromPreloaded])];
      if (!removed.length) return;
      this.localSettings.skills = (this.localSettings.skills ?? []).filter(s => valid.has(s));
      this.localSettings.activeSkills = (this.localSettings.activeSkills ?? []).filter(s => valid.has(s));
      this.$toast.warning({
        title: 'Skill removed',
        message: `The following skill(s) no longer exist and were removed: ${removed.join(', ')}. Save the chain to apply.`
      });
      this.onChange();
    },
    'localSettings.model': {
      handler() {
        const functions = this.getTools(this.localSettings.model).filter(f => f.isAllowed);
        if (!functions) {
          this.functionsExpanded = false;
          this.localSettings.functions = [];
        }

        const params = this.selectedModel?.params;
        if (params?.length) {
          this.localSettings.params = params.map(p => ({
            name: p.name,
            value: p.default ?? '',
            options: p.options
          }));
        } else {
          this.localSettings.params = [];
        }

        this.onChange();
      }
    }
  },
  created() {
    const model = this.modelBackwardCompatibility(this.localSettings.model);

    if (model && this.localSettings.model !== model) {
      this.localSettings.model = model;
    }

    this.$store.dispatch('nlp/getSkills');
  },
  methods: {
    getSkillLabel(name) {
      const skill = this.skills.find(s => s.name === name);
      return skill ? `${name} — ${skill.displayDescription}` : name;
    },
    isAllToolsAllowedByOrganizations() {
      const organizations = this.item?.organizations ?? [];

      if (!organizations.length) {
        return true;
      }

      const allowedTools = this.getTools(this.localSettings.model) ?? [];
      for (const tool of allowedTools.filter(t => t.organizations?.length)) {
        if (!organizations.every(org => tool.organizations?.includes(org))) {
          return false;
        }
      }

      return true;
    },
    isModelAllowed(model) {
      return model && this.allowedModels.includes(model);
    },
    modelBackwardCompatibility(candidate) {
      var value = this.models.find(m => m.id == candidate);

      if (value) {
        return value.id;
      }

      for (const md of this.models) {
        if (!md.config) {
          continue;
        }
        var config = JSON.parse(md.config);
        if (config.deployment == candidate) {
          return md.id;
        }
      }

      return null;
    },
    toggleFunctions() {
      this.functionsExpanded = !this.functionsExpanded;
    },
    isToolAllowed(toolName, modelId) {
      if (!toolName || !modelId) {
        return false;
      }

      const tool = this.tools.find(t => t.name === toolName);
      if (!tool) {
        return false;
      }
      const provider = this.providers.find(p => p.name === this.selectedModel?.provider);

      if (!provider?.toolsSupported) {
        return false;
      }

      return !tool.models?.length || tool.models.includes(modelId);
    },
    getTools(modelId) {
      if (!modelId) {
        return undefined;
      }

      const model = this.models.find(m => m.id == modelId);

      if (!model) {
        return undefined;
      }

      const provider = this.providers?.find(p => p.name === model.provider);

      if (provider?.toolsSupported) {
        return this.tools.map(f => ({
          ...f,
          isAllowed: this.isToolAllowed(f.name, modelId),
          selected: this.isToolAllowed(f.name, this.localSettings.model) && (this.localSettings.functions?.includes(f.name) ?? false)
        }));
      }

      return undefined;
    },
    functionChecked(name, checked) {
      if (checked) {
        this.localSettings.functions.push(name);
      } else {
        var index = this.localSettings.functions.indexOf(name);
        this.localSettings.functions.splice(index, 1);
      }

      this.onChange();
    },
    getLabel(id) {
      return this.models.find(m => m.id === id)?.name;
    },
    onChange() {
      this.$emit('change', {
        ...this.localSettings,
        functions: this.getTools(this.localSettings.model)
          .filter(f => f.selected)
          .map(f => f.name)
      });
    },
    toggleExpand() {
      this.expanded = !this.expanded;
    },
    deleteChat() {
      this.$emit('delete');
    }
  }
};
</script>

<style scoped lang="scss">
.accordion-item {
  .accordion-item-header {
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    align-items: center;
    align-content: center;
    min-height: 50px;
    height: 100%;
    background-color: var(--theme-surface);
    border-bottom: 1px solid var(--theme-on-surface);
    padding: 0 10px;

    .header-buttons {
      display: flex;
      align-items: center;

      .actions {
        display: flex;
        padding-right: 5px;
      }
    }
  }

  .title {
    display: grid;
    gap: 5px;
    align-items: center;
    grid-template-columns: max-content 1fr;

    .prefix {
      color: var(--theme-surface);
      background-color: var(--theme-secondary);
      border: 1px solid;
      border-radius: 3px;
      font-family: 'Roboto';
      font-weight: 800;
      padding: 2px;
      user-select: none;
    }
  }

  .button {
    padding: 0px;
  }

  .active {
    cursor: pointer;
    :hover {
      background-color: var(--theme-on-surface);
      color: var(--theme-surface);
      border-radius: 2px;
    }
  }

  .accordion-item-content {
    gap: 5px;
    .settings {
      padding: 5px;
      gap: 10px;

      span {
        font-size: 0.75rem;
        font-style: italic;
      }

      .error {
        color: red;
      }

      .model {
        padding: 5px;
      }

      .params {
        display: flex;
        flex-wrap: wrap;
        list-style: none;
        padding: 5px;
        gap: 10px;

        li {
          width: 150px;
        }
      }

      .functions {
        grid-column: 1 / span 3;
        grid-template-rows: auto 1fr;
        display: grid;
        gap: 10px;
        padding: 10px;
        padding: 5px;

        .list {
          background-color: var(--theme-surface);
          padding: 5px;
          .function {
            display: grid;
            grid-template-columns: 15px 1fr;
            gap: 10px;
            font-size: 0.85rem;
            padding: 5px;
            padding-left: 0;
            min-width: 150px;
            background-color: var(--theme-surface);

            .not-allowed {
              text-decoration: line-through;
              opacity: 0.6;
            }

            label {
              font-weight: bold;
              color: var(--theme-on-surface-accent);
            }
          }
        }
      }

      .history {
        padding: 5px;
      }

      .skills-selects {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 5px;
      }
    }

    span {
      font-size: 0.75rem;
      font-style: italic;
    }

    .error {
      color: red;
    }

    .expanded {
      display: flex;
      right: 0;
    }

    .prompt-body-content {
      padding: 10px;

      .template-content {
        margin: 10px 0;
        display: grid;
        gap: 10px;

        .label {
          font-weight: 500;
          font-size: 0.75rem;
          letter-spacing: 0.025em;
        }

        .editor {
          height: 200px;
        }

        .head {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      }
    }
  }
}
</style>
